import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Shield, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Saiyan.");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 terminal-grid opacity-30" />
      <div className="absolute inset-0 aura-glow" />
      <div className="scan-line" />
      <Link to="/" className="absolute top-6 left-6 text-zinc-400 hover:text-white text-xs uppercase tracking-widest inline-flex items-center gap-1" data-testid="login-back-home">
        <ChevronLeft className="w-4 h-4" /> Home
      </Link>

      <form onSubmit={submit} className="relative w-full max-w-md glass rounded-2xl p-8 fade-up aura-border-glow">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#FF6B00]/20 border border-[#FF6B00]/40 flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#FF6B00]" />
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Restricted Access</span>
            <h1 className="text-2xl font-bold text-white">Saiyan Studio</h1>
          </div>
        </div>

        <div className="mt-7 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Email</label>
            <input
              data-testid="login-email"
              autoFocus
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00] outline-none rounded-lg text-white text-sm"
              required
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00] outline-none rounded-lg text-white text-sm"
              required
            />
          </div>
        </div>

        <button
          data-testid="login-submit"
          type="submit"
          disabled={busy}
          className="aura-button mt-7 w-full h-12 rounded-full text-sm uppercase tracking-wider disabled:opacity-50"
        >
          {busy ? "Authenticating..." : "Power Up"}
        </button>

        <div className="mt-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button
          type="button"
          onClick={googleSignIn}
          data-testid="google-signin-btn"
          className="mt-4 w-full h-12 inline-flex items-center justify-center gap-3 rounded-full bg-white text-[#0a0a0a] font-bold text-sm hover:bg-zinc-100 transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.3-.4-3.5z"/>
          </svg>
          Sign in with Google
        </button>

        <p className="mt-4 text-[10px] text-center text-zinc-500 uppercase tracking-widest">Restricted to TheSaiyanVictoria channel owner</p>
      </form>
    </div>
  );
}
